import { FC, ReactNode } from "react";
import { formatRepeatingZeroDecimal } from "../../../hooks/formatRepeatingZeroDecimal";
import { AmountButtons } from ".";
import BuyBox from "./swap/tradeComponents/buyBox";
import cn from "classnames";
import styles from "../Swap.module.scss";
import { useSwap } from "@stores/swap.store";
import { IAsset } from "@api/IWalletController";
import { AssetButton } from "@components/common/AssetButton";

interface CurrencyFormProps {
    title: string;
    asset: IAsset | null;
    amount: string;
    setAmount?: (value: string) => void;
    balance?: number | string;
    slot?: ReactNode;
    handleSetCurrency: () => void;
    disableInput?: boolean;
    price?: number;
    isLoading?: boolean;
    seletable?: boolean;
    assetButton?: boolean;
    right?: boolean;
    percentButtons?: boolean;
    className?: string;
} // handleSetCurrency should be used as onClick for AssetButton

const CurrencyForm: FC<CurrencyFormProps> = ({
    title,
    asset,
    amount,
    setAmount,
    balance,
    slot,
    handleSetCurrency,
    disableInput = false,
    price,
    isLoading = false,
    seletable = true,
    assetButton = true,
    right = false,
    percentButtons = false,
    className = "",
}) => {
    const isBalanceSufficient = useSwap((state) => state.isBalanceSufficient);

    const renderUsdValue = () => {
        if (isLoading) {
            // Simple text indicator, replace with a spinner component if available
            return <span className="text-xs text-gray-400">Loading...</span>;
        }
        if (amount && price) {
            const value = price * parseFloat(amount);
            if (!isNaN(value)) {
                return `~$${value.toFixed(2)}`;
            }
        }
        return null; // Return null if no amount/price or calculation is invalid
    };

    return (
        <div className={cn("flex flex-col gap-2 relative", className)}>
            <div className={styles.sellTop}>
                <div className="text-sm font-medium">{title}</div>
                <div className="flex items-center gap-2">
                    {balance !== undefined && (
                        <div className={cn(styles.balance, "text-sm")}>
                            Balance:{" "}
                            <span className={!isBalanceSufficient() ? "text-primary" : ""}>
                                {balance !== undefined && !isNaN(Number(balance))
                                    ? formatRepeatingZeroDecimal(Number(balance), 14, 4)
                                    : balance}
                            </span>
                        </div>
                    )}
                    {slot}
                </div>
            </div>
            {assetButton && (
                <div
                    className={cn(
                        "flex justify-between w-full items-center",
                        right && "absolute right-0 z-10 bottom-2 max-w-fit",
                    )}
                >
                    {asset && <AssetButton asset={asset} seletable={seletable} onClick={handleSetCurrency} />}
                    {balance !== undefined && setAmount && percentButtons && (
                        <AmountButtons
                            balance={balance}
                            setAmount={setAmount} // Type is now guaranteed to be (value: string) => void
                            active={balance !== 0 && balance !== "connect wallet"}
                        />
                    )}
                </div>
            )}
            <div className="relative flex justify-between w-full items-center">
                <BuyBox
                    swapAmount={amount}
                    setSwapAmount={setAmount || (() => {})}
                    symbol={asset?.symbol ?? ""}
                    disabled={disableInput}
                />
                <span className="absolute top-0 right-0 h-full flex items-center">{renderUsdValue()}</span>
            </div>
        </div>
    );
};

export default CurrencyForm;
